// hooks/useCreateNewReservation.ts
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { RootState } from "../store"; // Adjust the import based on your file structure
import { InitializeNoPaymentData } from "../../api/types";
import Logger from "../helpers/logger";
import ErrorTracker from "../helpers/errorTracker";
import { noPaymentInitialized } from "../slice/bookingCreation/bookingCreationSlice";
import { notifyBookingFailure } from "../helpers/failureNotification";
import { buildAppUrl } from "../../util";

import { useInitializeNoPaymentMutation } from "../../api/payments/paymentsApiSlice";
import { bookingRecipient } from "../../util/bookingRecipient";

export const useInitializeNoPayment = ({ service }: { service: string }) => {
  const reservationIds = useSelector((state: RootState) =>
    state.bookingCreation.reservations.map(
      (reservation: any) => reservation.reservationId
    )
  );

  const personalContact = useSelector(
    (state: RootState) => state.bookingCreation.personalData
  );
  const hostContact = useSelector(
    (state: RootState) => state.bookingCreation.landlordData
  );

  const recipientSource = useSelector(
    (state: RootState) => ({
      isForSomeoneElse: state.bookingCreation.isForSomeoneElse,
      someoneElseData: state.bookingCreation.someoneElseData,
      recipientData: state.bookingCreation.recipientData,
    }),
    shallowEqual
  );

  const [initializeNoPaymentMutation] = useInitializeNoPaymentMutation();

  const dispatch = useDispatch();

  const initializeNoPayment = async () => {
    try {
      if (reservationIds.length && (personalContact || hostContact)) {
        const returnUrl = buildAppUrl(
          `/payments/no_payment/return?reservationIds=${reservationIds.join(
            ","
          )}`
        );

        const recipientData = bookingRecipient(recipientSource);

        const contact = service === "keys" ? hostContact : personalContact;
        const payload: InitializeNoPaymentData = {
          reservationIds: reservationIds,
          returnUrl,
          contact: contact,
          recipient: recipientData,
          service,
          };

        const response = await initializeNoPaymentMutation(payload).unwrap();

        if (response) {
          dispatch(noPaymentInitialized());
        }

        return response;
      }
    } catch (error) {
      Logger.for("createNewReservation").error(error);
      ErrorTracker.trackException(error as Error);
      // Confirming a free booking that fails left the customer on an
      // unresponsive button with no message at all (#1970).
      notifyBookingFailure(dispatch, error);
    }
  };

  return initializeNoPayment;
};
